import {
  AgreeToTermsPolicy,
  experience,
  ExtraParamsKey,
  SignInIdentifier,
  SignInMode,
} from '@logto/schemas';
import { useCallback, useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';

import LandingPageLayout from '@/Layout/LandingPageLayout';
import SingleSignOnFormModeContextProvider from '@/Providers/SingleSignOnFormModeContextProvider';
import SingleSignOnFormModeContext from '@/Providers/SingleSignOnFormModeContextProvider/SingleSignOnFormModeContext';
import WebAuthnContextProvider from '@/Providers/WebAuthnContextProvider';
import ContinueWithPhoneButton from '@/components/Button/ContinueWithPhoneButton';
import PasskeySignInButton from '@/components/Button/PasskeySignInButton';
import Divider from '@/components/Divider';
import GoogleOneTap from '@/components/GoogleOneTap';
import LastUsedChip from '@/components/LastUsedChip';
import TextLink from '@/components/TextLink';
import SocialSignInList from '@/containers/SocialSignInList';
import TermsAndPrivacyCheckbox from '@/containers/TermsAndPrivacyCheckbox';
import TermsAndPrivacyLinks from '@/containers/TermsAndPrivacyLinks';
import useLastUsedSignInMethod from '@/hooks/use-last-used-sign-in-method';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import { useSieMethods } from '@/hooks/use-sie';
import useTerms from '@/hooks/use-terms';

import ErrorPage from '../ErrorPage';

import Main from './Main';

/**
 * "Continue with phone" and the passkey button, below the form. Passkeys lead through
 * conditional UI (autofill on the identifier field), so the button is the fallback, not a
 * peer of the field.
 */
const SignInAlternates = () => {
  const { signInMethods, socialConnectors, passkeySignIn } = useSieMethods();
  const { showSingleSignOnForm } = useContext(SingleSignOnFormModeContext);

  const hasPhone = signInMethods.some(({ identifier }) => identifier === SignInIdentifier.Phone);
  // Phone only earns its own button when the inline form has another method to lead with.
  const hasNonPhoneInline = signInMethods.some(
    ({ identifier }) => identifier !== SignInIdentifier.Phone
  );
  const showPhone = hasPhone && hasNonPhoneInline;
  const hasPasskey = Boolean(passkeySignIn?.enabled && passkeySignIn.showPasskeyButton);

  if (showSingleSignOnForm || !(showPhone || hasPasskey)) {
    return null;
  }

  // With social leading the page the "or" divider already sits above the form.
  const isSocialPrimary = socialConnectors.length > 0 && signInMethods.length > 0;

  return (
    <>
      {!isSocialPrimary && <Divider label="description.or" className="my-6" />}
      <div className={isSocialPrimary ? 'mt-3 flex flex-col gap-3' : 'flex flex-col gap-3'}>
        {showPhone && <ContinueWithPhoneButton mode="signIn" />}
        {hasPasskey && <PasskeySignInButton />}
      </div>
    </>
  );
};

const SignInFooters = () => {
  const { t } = useTranslation();
  const { termsValidation, agreeToTermsPolicy } = useTerms();
  const navigate = useNavigateWithPreservedSearchParams();

  const { signInMethods, signUpMethods, socialConnectors, signInMode, singleSignOnEnabled } =
    useSieMethods();

  const { showSingleSignOnForm } = useContext(SingleSignOnFormModeContext);

  const isSsoLastUsed = useLastUsedSignInMethod()?.type === 'sso';

  const handleSsoNavigation = useCallback(async () => {
    if (agreeToTermsPolicy === AgreeToTermsPolicy.Manual && !(await termsValidation())) {
      return;
    }
    navigate('/single-sign-on/email');
  }, [agreeToTermsPolicy, navigate, termsValidation]);

  const showCreateAccount = signInMode === SignInMode.SignInAndRegister && signUpMethods.length > 0;

  if (showSingleSignOnForm || (!singleSignOnEnabled && !showCreateAccount)) {
    return null;
  }

  return (
    <div className="mt-6 flex flex-col gap-3 text-center text-sm text-muted first:mt-0">
      {singleSignOnEnabled && (
        <>
          {/* The chip is a sibling of the link so it never leaks into the link's accessible name. */}
          <div className="flex flex-wrap items-center justify-center gap-x-1 gap-y-1">
            <span>
              <Trans components={{ a: <TextLink onClick={handleSsoNavigation} /> }}>
                {t('description.use_single_sign_on')}
              </Trans>
            </span>
            {isSsoLastUsed && <LastUsedChip className="ms-1" />}
          </div>
          {
            // SSO is the only method, so the agreement has to be collected here.
            signInMethods.length === 0 &&
              socialConnectors.length === 0 &&
              agreeToTermsPolicy === AgreeToTermsPolicy.Manual && (
                // `text-start`: the footer's centring would pull the label away from its box.
                <TermsAndPrivacyCheckbox className="justify-center text-start" />
              )
          }
        </>
      )}
      {showCreateAccount && (
        <div>
          {t('description.no_account')}{' '}
          <TextLink replace to="/register" text="action.create_account" />
        </div>
      )}
    </div>
  );
};

/** Social providers lead the page, above the "or" divider and the identifier form. */
const SocialSignInPrimary = () => {
  const { signInMethods, socialConnectors } = useSieMethods();
  const { showSingleSignOnForm } = useContext(SingleSignOnFormModeContext);

  // With no sign-in methods `Main` renders this same list as the whole page.
  if (showSingleSignOnForm || socialConnectors.length === 0 || signInMethods.length === 0) {
    return null;
  }

  return (
    <>
      <SocialSignInList socialConnectors={socialConnectors} />
      <Divider label="description.or" className="my-6" />
    </>
  );
};

const SignIn = () => {
  const { signInMethods, socialConnectors, signInMode } = useSieMethods();
  const { agreeToTermsPolicy } = useTerms();
  const [params] = useSearchParams();

  if (!signInMode) {
    return <ErrorPage />;
  }

  if (signInMode === SignInMode.Register) {
    return <Navigate to="/register" />;
  }

  if (params.get(ExtraParamsKey.OneTimeToken)) {
    return (
      <Navigate
        replace
        to={{ pathname: `/${experience.routes.oneTimeToken}`, search: `?${params.toString()}` }}
      />
    );
  }

  return (
    <LandingPageLayout
      title="description.sign_in_to_your_account"
      description="description.auth_subtitle"
    >
      <GoogleOneTap context="signin" />
      <WebAuthnContextProvider>
        <SingleSignOnFormModeContextProvider>
          <div className="flex w-full flex-col">
            <SocialSignInPrimary />
            <Main signInMethods={signInMethods} socialConnectors={socialConnectors} />
            <SignInAlternates />
            <SignInFooters />
            {agreeToTermsPolicy !== AgreeToTermsPolicy.Manual && (
              <TermsAndPrivacyLinks className="mt-6 text-center text-xs text-muted first:mt-0" />
            )}
          </div>
        </SingleSignOnFormModeContextProvider>
      </WebAuthnContextProvider>
    </LandingPageLayout>
  );
};

export default SignIn;
